import { ReactNode, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DisableMachineInput,
  useDisableMachineMutation,
} from '../../api/graphql/generated';
import { logger } from '../../util/logger';
import { useRemoveDirectConnectCachedMachine } from '../cache/direct-connect-machine-cache';
import { useDirectConnectRemoveAllTask } from '../cache/direct-connect-task-cache';
import { useDirectConnectGoBack } from '../helper/direct-connect-helper';
import {
  DirectConnectTask,
  DirectConnectTaskType,
} from '../model/direct-connect.model';
import { DirectConnectGateSource } from './direct-connect-gate-keeper';
import {
  DirectConnectTaskExecutionResponse,
  DirectConnectTaskGate,
} from './direct-connect-task-gate';

interface DirectConnectDeactivateGateProps {
  children: ReactNode;
}

const DirectConnectDeactivateGate = (
  props: DirectConnectDeactivateGateProps
) => {
  const { children } = props;
  const { i18n } = useTranslation();
  const goBack = useDirectConnectGoBack();

  const [disableMachine] = useDisableMachineMutation();
  const removeCachedMachine = useRemoveDirectConnectCachedMachine();
  const removeAllTasks = useDirectConnectRemoveAllTask();

  const executeTask = useCallback(
    async (
      task: DirectConnectTask<DisableMachineInput>
    ): Promise<DirectConnectTaskExecutionResponse> => {
      const { payload } = task;

      logger.info('disabling machine');

      await disableMachine({
        variables: {
          input: payload,
        },
      });

      removeCachedMachine(payload.id);
      removeAllTasks(task.serialNumber);

      return {
        success: true,
      };
    },
    [disableMachine, removeCachedMachine, removeAllTasks]
  );

  const onSuccess = useCallback(async () => {
    logger.info('executing deactivate gate on success');
    goBack();
  }, [goBack]);

  return (
    <DirectConnectTaskGate
      source={DirectConnectGateSource.DEACTIVATE_GATE}
      taskType={DirectConnectTaskType.DEACTIVATE}
      executeTask={executeTask}
      loaderMessage={i18n.t('common:direct_connect_deactivating_robot')}
      defaultErrorMessage={i18n.t(
        'common:direct_connect_synchronizing_general_error'
      )}
      onSuccess={onSuccess}
    >
      {children}
    </DirectConnectTaskGate>
  );
};

export { DirectConnectDeactivateGate };
